/**
 * Cookie-less page-view beacon (spec §3). Nothing is stored on the device: the entry source (utm_source,
 * utm_campaign, ?c=) lives in module memory for this page load only; the referrer goes with the first event.
 * The path leaves the device already as its template (`/ru/a/:id`), so share ids and invite codes never do.
 */
import { AN_CLIENT_TRAITS, AN_EXCLUDED_ROLES, normalizeAnPath, type AnBeacon } from '@tg/shared';

const URL_ = '/api/a/e';

export interface BeaconEnv {
  search: string;
  referrer: string;
  width: number;
  touch: boolean;
}

let entry: Pick<AnBeacon, 'us' | 'uc' | 'c'> | null = null;
let referrerSent = false;
let lastPath: string | null = null;

/** tests only */
export function __resetBeacon(): void {
  entry = null;
  referrerSent = false;
  lastPath = null;
}

export function optedOut(nav: Navigator = navigator): boolean {
  const n = nav as Navigator & { globalPrivacyControl?: boolean };
  const w = typeof window === 'undefined' ? undefined : (window as Window & { doNotTrack?: string | null });
  return n.doNotTrack === '1' || w?.doNotTrack === '1' || n.globalPrivacyControl === true || n.webdriver === true;
}

/** "/ru/a/X7K" → "/ru/a/:id"; a path the server would drop anyway → null. */
export function beaconPath(pathname: string): string | null {
  const n = normalizeAnPath(pathname);
  if (!n) return null;
  if (!n.lang) return n.path;
  return n.path === '/' ? `/${n.lang}` : `/${n.lang}${n.path}`;
}

function currentEnv(): BeaconEnv {
  return { search: location.search, referrer: document.referrer, width: window.innerWidth, touch: (navigator.maxTouchPoints ?? 0) > 0 };
}

export function buildBeacon(pathname: string, theme: 'dark' | 'light', env: BeaconEnv = currentEnv()): AnBeacon {
  if (!entry) {
    const q = new URLSearchParams(env.search);
    const pick = (k: string, max: number) => q.get(k)?.slice(0, max) || undefined;
    entry = {};
    const us = pick('utm_source', 64);
    const uc = pick('utm_campaign', 64);
    const c = pick('c', 8);
    if (us) entry.us = us;
    if (uc) entry.uc = uc;
    if (c) entry.c = c;
  }
  const b: AnBeacon = { p: (beaconPath(pathname) ?? pathname).slice(0, 200), th: theme === 'light' ? 'l' : 'd', ...entry };
  if (!referrerSent) {
    referrerSent = true;
    if (env.referrer) b.r = env.referrer.slice(0, 500);
  }
  if (AN_CLIENT_TRAITS) {
    b.w = Math.max(0, Math.min(20_000, Math.round(env.width)));
    b.t = env.touch ? 1 : 0;
  }
  return b;
}

/**
 * One event per pathname change. `role` is the signed-in user's role when known: excluded roles (admins)
 * send nothing — the server drops them too, this only saves the request.
 */
export function sendPageview(pathname: string, theme: 'dark' | 'light', role?: string): void {
  if (pathname === lastPath) return;
  lastPath = pathname;
  if (typeof navigator === 'undefined' || optedOut() || (document.visibilityState as string) === 'prerender') return;
  if (role && (AN_EXCLUDED_ROLES as readonly string[]).includes(role)) return;
  if (beaconPath(pathname) === null) return;
  const body = JSON.stringify(buildBeacon(pathname, theme));
  try {
    if (navigator.sendBeacon?.(URL_, new Blob([body], { type: 'text/plain' }))) return;
  } catch {
    /* fall back to fetch */
  }
  void fetch(URL_, {
    method: 'POST',
    body,
    keepalive: true,
    credentials: 'include',
    headers: { 'content-type': 'text/plain', 'x-requested-with': 'tg' },
  }).catch(() => undefined);
}
